/**
 * SearchResults Component - Display search results with relevance scores
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  TrendingUp as RelevanceIcon,
} from '@mui/icons-material';

export interface SearchResult {
  id: string;
  supplier_name: string;
  declaration_type: string;
  product_description: string;
  hs_code?: string;
  origin_country?: string;
  valid_until?: string;
  status: string;
  relevance_score: number;
  snippet?: string;
}

interface SearchResultsProps {
  results: SearchResult[];
  loading?: boolean;
  query: string;
  onViewDetails?: (declarationId: string) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  loading = false,
  query,
  onViewDetails,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expiring_soon':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getRelevanceColor = (score: number) => {
    if (score >= 0.7) return 'success';
    if (score >= 0.4) return 'warning';
    return 'default';
  };

  const highlightText = (text: string, searchQuery: string) => {
    if (!searchQuery || !text) return text;
    
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '0 2px' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Searching...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!query) {
    return null;
  }

  if (results.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body1" gutterBottom>
          No results found for "{query}"
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try different keywords or check your spelling
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Found {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
      </Typography>

      {results.map((result) => (
        <Card key={result.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" component="div">
                    {highlightText(result.supplier_name, query)}
                  </Typography>
                  <Chip
                    label={result.declaration_type}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={result.status}
                    size="small"
                    color={getStatusColor(result.status)}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {highlightText(result.product_description, query)}
                </Typography>

                {result.snippet && (
                  <Typography
                    variant="body2"
                    sx={{
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      fontStyle: 'italic',
                      mb: 1,
                    }}
                  >
                    ...{highlightText(result.snippet, query)}...
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {result.hs_code && (
                    <Typography variant="caption" color="text.secondary">
                      HS Code: {highlightText(result.hs_code, query)}
                    </Typography>
                  )}
                  {result.origin_country && (
                    <Typography variant="caption" color="text.secondary">
                      Origin: {highlightText(result.origin_country, query)}
                    </Typography>
                  )}
                  {result.valid_until && (
                    <Typography variant="caption" color="text.secondary">
                      Valid Until: {result.valid_until}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, ml: 2 }}>
                <Chip
                  icon={<RelevanceIcon />}
                  label={`${(result.relevance_score * 100).toFixed(0)}%`}
                  size="small"
                  color={getRelevanceColor(result.relevance_score)}
                  variant="outlined"
                />
                {onViewDetails && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => onViewDetails(result.id)}
                  >
                    View Details
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default SearchResults;
